import React, { useEffect, useState } from 'react'
import { ProjectProps } from '../Project'
import { FunctionModel } from '../../../utils/interfaces'
import { useTranslation } from 'react-i18next'
import { BoldHeader3 } from '../../../components/General'
import { useEventListener } from '../../../utils/functions'
import { Input } from '../../../components/Input'
import Loading from '../../../components/Loading'
import { createItem, deleteItems, updateItem } from '../../../api/API'
import { tables } from '../../../utils/constants'
import { getNewFunctionId } from './FunctionUtils'
import { useNavigate } from 'react-router-dom'
import AlertWrapper from '../../../components/AlertWrapper'
import Alert from '../../../components/Alert'

interface Props extends ProjectProps {
  func: FunctionModel
  setFunc: React.Dispatch<React.SetStateAction<FunctionModel | null>>
}

const FunctionConfig = (props: Props) => {

  const { t } = useTranslation();
  const navigate = useNavigate();
  const [name, setName] = useState(props.func.name);
  const [isSaving, setIsSaving] = useState(false);
  const [isChangingId, setIsChangingId] = useState(false);
  const [showChangeIdAlert, setShowChangeIdAlert] = useState(false);

  useEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    if (!target.closest('.change-id-alert') && !target.closest('.change-id-button')) {
      setShowChangeIdAlert(false);
    }
  }, []);

  useEffect(() => {
    setName(props.func.name);
  }, [props.func]);

  const saveName = async () => {
    setIsSaving(true);
    const newFunction: FunctionModel = { ...props.func, name, lastUpdate: new Date().toISOString() };
    await updateItem(tables.functions, newFunction.id, { name, lastUpdate: new Date().toISOString() });
    props.setFunc(newFunction);
    setIsSaving(false);
  }

  const changeId = async () => {
    setIsChangingId(true);
    const newFunction: FunctionModel = { ...props.func, id: getNewFunctionId(), lastUpdate: new Date().toISOString() };
    await createItem(tables.functions, newFunction);
    await deleteItems(tables.functions, [props.func.id]);
    navigate(`/project/${props.project.id}/function/${newFunction.id}`);
    setIsChangingId(false);
    setShowChangeIdAlert(false);
  }

  const updateAutoSave = async () => {
    const newFunction: FunctionModel = { ...props.func, autoSave: !props.func.autoSave, lastUpdate: new Date().toISOString() };
    props.setFunc(newFunction);
    await updateItem(tables.functions, newFunction.id, { autoSave: !props.func.autoSave, lastUpdate: new Date().toISOString() });
  }

  return (
    <>
      <div className='max-w-3xl'>
        <BoldHeader3>{t('project.function.config')}</BoldHeader3>
        <div className='mt-4'>
          <div>
            <label className='text-xs m-1 text-gray-400'>ID</label>
            <div className='text-sm m-1'>{props.func.id}</div>
            <div className='text-sm text-gray-400 m-1 mt-2 flex items-center flex-wrap gap-2'>{t('project.function.config.id-description')}<span onClick={() => window.open(`/docs?d=function`)} className='font-light cursor-pointer flex items-center text-primary underline'>{t('project.api-docs')}</span></div>
            <button onClick={() => setShowChangeIdAlert(true)} className='mt-2 change-id-button text-xs py-1.5 px-6 rounded-lg bg-primary text-white'>{t('project.function.config.id-regenerate')}</button>
          </div>
        </div>
        <div className='mt-8'>
          <div>
            <label className='text-xs m-1 text-gray-400'>{t('general.name')}</label>
            <Input textSize='small' value={name} onChange={(e) => setName(e.target.value)} />
            <button onClick={saveName} disabled={isSaving || name === props.func.name || name === ''} className='mt-2 flex items-center justify-center text-xs h-7 px-6 rounded-lg bg-primary disabled:bg-gray-300 text-white'>{isSaving ? <Loading /> : t('general.save')}</button>
          </div>
        </div>
        <div className='mt-8'>
          <div>
            <label className='text-xs m-1 text-gray-400'>{t('project.function.config.auto-save')}</label>
            <div onClick={updateAutoSave} className='flex items-center p-2 cursor-pointer'>
              <button className={`flex items-center justify-center text-2xs w-4 h-4 rounded-full border ${props.func.autoSave ? 'bg-primary border-primary' : 'bg-white'}`}><i className="fa-solid fa-check text-white" /></button>
              <div className='ml-2 text-sm'>{t('project.function.config.auto-save.description')}</div>
            </div>
          </div>
        </div>
      </div>

      {/* change id */}
      {showChangeIdAlert && <AlertWrapper>
        <Alert
          extraclass='change-id-alert'
          title={t('project.function.config.id-regenerate')}
          maxWidth={400}
          onClose={() => setShowChangeIdAlert(false)}
          body={<div>
            <div>{t('project.function.config.id-regenerate.description')}</div>
          </div>}
          bottom={<div>
            <div className='flex'><button onClick={changeId} disabled={isChangingId} className='flex items-center justify-center bg-primary disabled:bg-gray-300 text-white px-8 py-2 rounded-lg text-sm'>{isChangingId ? <Loading /> : t('general.update')}</button></div>
          </div>}
        />
      </AlertWrapper>}
    </>
  )
}

export default FunctionConfig